import normalizeText from '@/lib/normalizeWhitespaceText.js';

const getScheduleDateTimestamp = (value) => {
  const timestamp = new Date(value).getTime();

  if (!Number.isFinite(timestamp)) {
    return 0;
  }

  return timestamp;
};

const getSafeScheduleRevision = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 1) {
    return 1;
  }

  return Math.floor(numericValue);
};

export const normalizeScheduleItem = (schedule = {}) => {
  const id = normalizeText(schedule?.id);

  if (!id) {
    return null;
  }

  const title = normalizeText(schedule?.title) || 'Untitled schedule';
  const prompt = String(schedule?.prompt || '').trim();
  const promptSummary = normalizeText(schedule?.promptSummary) || null;
  const updatedAt = normalizeText(schedule?.updatedAt) || new Date().toISOString();
  const createdAt = normalizeText(schedule?.createdAt) || updatedAt;
  const threadId = normalizeText(schedule?.threadId) || null;
  const pinned = Boolean(schedule?.pinned || schedule?.pinnedAt || schedule?.pinnedOn);
  const pinnedAt = pinned ? normalizeText(schedule?.pinnedAt || schedule?.pinnedOn || schedule?.updatedAt || schedule?.createdAt) || updatedAt : null;
  const scheduleType = schedule?.scheduleType === 'fixed' || schedule?.scheduleType === 'interval' ? schedule.scheduleType : null;
  const interval = typeof schedule?.interval === 'number' && schedule.interval > 0 ? schedule.interval : null;
  const runTimes = Array.isArray(schedule?.runTimes) ? schedule.runTimes.filter((value) => typeof value === 'string' && value.trim()) : [];
  const days = Array.isArray(schedule?.days) ? schedule.days.filter((value) => typeof value === 'number' && value >= 0 && value <= 6) : [];
  const timeZone = normalizeText(schedule?.timeZone) || null;

  return {
    id,
    title,
    active: Boolean(schedule?.active),
    pinned,
    pinnedAt,
    cadenceSummary: normalizeText(schedule?.cadenceSummary) || 'No cadence configured',
    nextRunSummary: normalizeText(schedule?.nextRunSummary) || null,
    prompt,
    promptSummary,
    threadId,
    updatedAt,
    createdAt,
    revision: getSafeScheduleRevision(schedule?.revision),
    scheduleType,
    interval,
    runTimes,
    days,
    timeZone,
    composerModel: normalizeText(schedule?.composerModel) || null,
    composerReasoningEffort: normalizeText(schedule?.composerReasoningEffort) || null,
    actionSummary: normalizeText(schedule?.actionSummary) || null,
    workflowStepId: normalizeText(schedule?.workflowStepId) || null
  };
};

const mapApiScheduleToLocal = (apiSchedule) => {
  if (!apiSchedule || typeof apiSchedule !== 'object') {
    return null;
  }

  const scheduleId = normalizeText(apiSchedule.id || apiSchedule.scheduleId);
  const threadId = normalizeText(apiSchedule.threadId);

  if (!scheduleId) {
    return null;
  }

  const config = apiSchedule.scheduleConfig && typeof apiSchedule.scheduleConfig === 'object' ? apiSchedule.scheduleConfig : {};

  return normalizeScheduleItem({
    ...apiSchedule,
    id: scheduleId,
    threadId,
    updatedAt: apiSchedule.updatedAt || apiSchedule.updatedOn,
    createdAt: apiSchedule.createdAt || apiSchedule.createdOn,
    promptSummary: apiSchedule.promptSummary || config.promptSummary,
    scheduleType: config.scheduleType,
    interval: config.interval,
    runTimes: config.runTimes,
    days: config.days,
    timeZone: config.timeZone || apiSchedule.timezone,
    composerModel: apiSchedule.composerModel || config.composerModel,
    composerReasoningEffort: apiSchedule.composerReasoningEffort || config.composerReasoningEffort
  });
};

export const sortSchedules = (items = []) => {
  return [...items].sort((left, right) => {
    const leftPinned = Boolean(left?.pinned);
    const rightPinned = Boolean(right?.pinned);

    if (leftPinned !== rightPinned) {
      return Number(rightPinned) - Number(leftPinned);
    }

    if (leftPinned && rightPinned) {
      const pinnedAtDiff = getScheduleDateTimestamp(right?.pinnedAt || right?.updatedAt || right?.createdAt || 0) - getScheduleDateTimestamp(left?.pinnedAt || left?.updatedAt || left?.createdAt || 0);

      if (pinnedAtDiff !== 0) {
        return pinnedAtDiff;
      }
    }

    const updatedAtDiff = getScheduleDateTimestamp(right?.updatedAt || 0) - getScheduleDateTimestamp(left?.updatedAt || 0);

    if (updatedAtDiff !== 0) {
      return updatedAtDiff;
    }

    return String(left?.title || '').localeCompare(String(right?.title || ''));
  });
};

export const matchesScheduleId = (schedule, requestedId) => {
  const normalizedRequestedId = normalizeText(requestedId);

  if (!normalizedRequestedId) {
    return false;
  }

  return normalizeText(schedule?.id) === normalizedRequestedId;
};

export const getScheduleItemsFromListResult = (result = {}) => {
  const items = Array.isArray(result?.data?.items) ? result.data.items : Array.isArray(result?.items) ? result.items : [];

  return items.map((item) => mapApiScheduleToLocal(item)).filter(Boolean);
};

export const getScheduleItemFromResult = (result = {}) => {
  return mapApiScheduleToLocal(result?.data?.schedule || result?.schedule || null);
};
